import {createStore} from 'vuex'
import Router from '../router/Router.js';
import autosize from 'autosize';
import {PortfolioActions} from "./actions/PortfolioActions.js";
import UsersAPI from "../api/users/UserAPI.js";
import {event} from 'vue-gtag';
import {
    ChecklistInteractionEnum,
    DesignPanelPathsEnum,
    EditedImageTypeEnum,
    PageTypesEnum,
    SectionTypesEnum
} from "../common/enums.js";
import {aboutPageSections, basicSections, homePageRecommendedSections, uxMethods} from "../sections/_SectionTypes.js";
import {review} from './modules/reviewStore.js';
import {design} from './modules/designStore.js';
import {view} from './modules/viewStore/viewStore.js';
import {jfc} from './modules/jfcStore/jfcStore.js';
import {presetEditor} from './modules/presetEditorStore.js';
import {defaultMockup} from "@/scripts/common/mockupStore/mockupStore.js";
import MixpanelService from "@/scripts/analytics/mixpanel.js";
import ProjectsAPI from "@/scripts/api/projects/ProjectsAPI.js";
import PortfoliosAPI from "@/scripts/api/portfolios/PortfoliosAPI.js";
import AdminAPI from "@/scripts/api/admin/AdminAPI.js";

import portfolioMutations from "@/scripts/store/mutations/portfolioMutations.js";
import projectMutations from "@/scripts/store/mutations/projectMutations.js";
import sectionMutations from "@/scripts/store/mutations/sectionMutations.js";
import sidebarMutations from "@/scripts/store/mutations/sidebarMutations.js";

import portfolioGetters from "@/scripts/store/getters/portfolioGetters.js";
import projectGetters from "@/scripts/store/getters/projectGetters.js";
import sectionGetters from "@/scripts/store/getters/sectionGetters.js";
import sidebarGetters from "@/scripts/store/getters/sidebarGetters.js";
import {deepMergePlugin} from "@/scripts/store/plugins/deepMergePlugin.js";
import templateGetters from "@/scripts/store/getters/templateGetters.js";
import templateMutations from "@/scripts/store/mutations/templateMutations.js";
import sectionActions from "@/scripts/store/actions/SectionActions.js";

import {getModuleContent} from "@/scripts/common/utilities/generalSection.js";

const cloneDesignSetting = (value) => value == null ? value : JSON.parse(JSON.stringify(value));

/* Create a reactive store.state */
export const store = createStore({
    modules: {
        review,
        design,
        view,
        jfc,
        presetEditor
    },
    plugins: [deepMergePlugin],
    state() {
        return {
            triggerAnimation: true,
            recalculateScrollTrigger: true,
            navigatedFromPreview: false,
            emergencyModeSettings: null,
            user: null,
            userPortfolios: null,
            portfolio: null,
            portfolioSetupComplete: false,
            portfolioPages: null,
            caseStudyPages: null,
            allCaseStudyPages: null,
            allPortfolios: null,
            selectedPortfolioForDomain: null,
            userSettings: {
                currentSubscription : null,
                currentSubscriptionPlan : null,
                domainSubscriptions: [],
                invoices: [],
                stripePrices: {
                    monthlyPrice: {
                        id:'',
                        price:'NaN'
                    },
                    yearlyPrice: {
                        id:'',
                        price:'NaN'
                    }
                },
                referrerData:null,
                referredData:null,
                creditBalance:0,
                showSupportSurvey: false,
                showTrialStartedAlert: false,
                showTrialRestartedAlert: false
            },
            project: null,
            allowedSections: null,
            sectionEditorSettings: {
                pageToEdit: null,
                emptyStateTreshold: 1,
                hideEditButtons: false,
                hideMockupButtons: false,
                hideTextAndImageIdeasButtons: false,
                hiddenMediaTypes: {
                    image: false,
                    video: false,
                    proto: false,
                    media: false,
                    mockup:false
                },
                maxColumnsPerSection: 4,
                maxStatItemsPerSection: 5,
                defaultPadding: 60,
                sectionLimit: null,
                ctsModuleSelected: false,
                pendingSettingsReopen: null,
                pendingGalleryFocus: null
            },
            selectedProject: null,
            selectedSection: null,
            selectedPortfolio: null,
            savingProject: false,
            activeModal: null,
            portfolioHeroFontStylesBeforeLayoutSwitch: null,
            portfolioHeroNavigationBeforeLayoutSwitch: null,
            portfolioHeroBackgroundBeforeLayoutSwitch: null,
            textEditorActive: false,
            isViewLight: () => {
                let path = Router.currentRoute._value.fullPath;
                return (path.includes('/create-new-portfolio')
                    || path.includes('/home/settings')
                    || path === '/home/portfolios'
                    || path === '/home/notifications'
                    || path === '/home/prices'
                    || path.includes('/home/domain')
                    || path.includes('/home/url')
                    || path.includes('/home/payment')
                    || path.startsWith('/reviews'))
            },
            activeModalData: null,
            activeSectionSettingId: null,
            uploadImageAndCropStep: "upload",
            uploadImageAndCropType: null,
            alertModalMessage: null,
            showSidePanel: false,
            sidePanelSettings: {
                panel: null,
                title: 'Case study design',
                previousSettings: null,
                prevAccentColor: null,
                saveButton: 'Save',
                showSecondaryTab: false,
                hideBackButton: false,
                hideActionButtons: false,
                allowSave: false,
                portfolioPassword: null,
                newPageDetails: null,
                globalSpacingChanged: false,
                activeModal: null,
                goBackPath: null,
                updateParentPortfolio: false,
                tabPills: null,
                activeTab: null,
                storedScrollPosition: null,
                selectedColorPaletteItem: null,
                showInfoAboveCtas: false,
                infoAboveCtasMessage: null,
                infoAboveCtasLinkText: null,
                infoAboveCtasLinkUrl: null,
                editedCtaType: null,
            },
            currentSubscriptionPlan: null,
            subscriptionSettingsInfo: null,
            zoomData: {
                isZoomed:false,
                imageUrl:null,
                imageRef:null
            },
            selectedMockupStyle: null,
            showTipMessage: false,
            selectedStatSectionStyles: null,
            selectedTestimonialSectionStyles: null,
            selectedHeaderSectionStyle: null,
            alertMessage: {
                show: false,
                type: 'info',
                message: null,
                interval: null,
            },

            notifications: [],
            showArchiveInfoBubble: false,
            showOnBoardingScreen: false,
            onboardingFinished: false,
            templateTipMessageSeen: false,
            pageIsLoading: false,
            loaderMessage: null,
            creatingNewPortfolio: false,
            previewingTemplate:false,
            templatePreviews: [],
            portfolioTemplates:[],
            previewAndCreatePortfolioDetails: {
                selectedPortfolioTemplate:null,
                activeView:0,
                selectedProjects:[],
                newPortfolio: null
            },
            fonts:[],
            fontPairs:[],
            reviewCenter: {
                reviewSent: false,
                askForReviewProjectId: null
            },
            darkMode:null,
            displayToggleGrid: false,
            checklist: {
                activeInteraction:null,
                subpage:false,
                finished:false,
                popUpClosed:true
            },
            blockedEditor: false,
            subscriptionExpiredModalShown: false,
            showPublishTooltip: false,
            eventsToTrack: [],
            showAiFeedbackModal:false,
            aiFeedbackModalType: null,
            portfolioTemplateStyles:[],
            sectionPresets: [],
            sectionPresetsLoaded: false,
            isGlobalCtsOff:false,
            activeChecklistItems:[
                ChecklistInteractionEnum.TEMPLATE,
                ChecklistInteractionEnum.CUSTOMIZE,
                ChecklistInteractionEnum.HEADLINE,
                ChecklistInteractionEnum.CASE_STUDY,
                ChecklistInteractionEnum.THUMBNAIL,
                ChecklistInteractionEnum.PUBLISH
            ],
            cursorPreviewMode: false
        }
    },
    mutations: {
        ...portfolioMutations,
        ...projectMutations,
        ...sectionMutations,
        ...sidebarMutations,
        ...templateMutations,
        setSelectedPortfolioForDomain(state, portfolioId) {
            state.selectedPortfolioForDomain = portfolioId;
        },

        clearSelectedPortfolioForDomain(state) {
            state.selectedPortfolioForDomain = null;
        },
        resetZoom(state) {
            state.zoomData = {
                isZoomed:false,
                imageUrl:null,
                imageRef:null
            }
        },
        setZoom(state, payload) {
            state.zoomData = {
                isZoomed:true,
                imageUrl:payload.url,
                imageRef:payload.ref
            }
        },
        setPageToEdit(state, shortId) {
            if(state.portfolioPages) {
                store.commit("replaceProject", state.portfolioPages.find(p => p.shortId === shortId));
                store.commit("setAllowedSections", state.project.type);
                store.commit("setSectionEditorSettings", {
                    pageToEdit: !!state.project ? state.project.type : 'blank',
                    sectionLimit: 3
                });
            }
            state.navigatedFromPreview = false;
        },
        setNavigateFromPreview(state, value) {
            state.navigatedFromPreview = value;
        },
        setCaseStudyToEdit(state, shortId) {
            store.commit("replaceProject", state.caseStudyPages.find(p => p.shortId === shortId));
            store.commit("setAllowedSections", "caseStudy");
        },
        updateEditedPageSectionsInPortfolioPages(state) {
            state.portfolioPages.find(p => p.shortId === state.project.shortId).sections = state.project.sections;
        },
        updateEditedCaseStudySectionsInCaseStudyPages(state) {
            state.caseStudyPages.find(p => p.shortId === state.project.shortId).sections = state.project.sections;
        },
        updateEditedCaseStudyInCaseStudyPages(state) {
            const index = state.caseStudyPages.findIndex(cs => cs._id === state.project._id);
            if (index !== -1) {
                state.caseStudyPages.splice(index, 1, state.project);
            }
        },
        addNewPageToList(state, page) {
            state.portfolioPages.push(page);
        },
        addNewCaseStudyToList(state, caseStudy) {
            state.caseStudyPages.push(caseStudy);
        },
        setActiveModal(state, modal) {
            state.activeModal = modal;
        },
        setActiveSectionSetting(state, sectionId) {
            state.activeSectionSettingId = sectionId;
        },
        clearActiveSectionSetting(state, sectionId) {
            if (state.activeSectionSettingId === sectionId) {
                state.activeSectionSettingId = null;
            }
        },
        setAllowedSections(state, type) {
            state.allowedSections = Object.assign({}, {
                basicSections: type !== PageTypesEnum.CASE_STUDY ? aboutPageSections : basicSections,
                recommendedSections: type !== PageTypesEnum.CASE_STUDY ? homePageRecommendedSections : uxMethods
            });
        },
        setSectionPresets(state, presets) {
            state.sectionPresets = presets;
            state.sectionPresetsLoaded = true;
        },
        setSectionEditorSettings(state, obj) {
            state.sectionEditorSettings = Object.assign({}, store.state.sectionEditorSettings, obj);
        },
        selectCaseStudy(state, caseStudy) {
            state.selectedProject = caseStudy
        },
        setCurrentPlan(state, plan) {
            state.currentSubscriptionPlan = plan;
        },
        setCurrentSubscription(state, subscription) {
            state.userSettings.currentSubscription = {...subscription};
        },
        updateSectionByIndex(state, obj) {
            state.project.sections[obj.sectionIndex] = {
                ...state.project.sections[obj.sectionIndex],
                ...obj.updatedSectionEntries
            }
        },
        updateProject(state, project) {
            store.state.project = Object.assign({}, store.state.project, project);
        },
        updateSavingState(state, value) {
            if (typeof value === "boolean") {
                state.savingProject = value;
                return;
            }
            state.savingProject = !state.savingProject;
        },
        updateSidePanel(state, panelStates) {
            state.sidePanelSettings = Object.assign({}, store.state.sidePanelSettings, panelStates);
        },
        updateEventsToTrackList(state, newEvent) {
            const mixpanelBaseProps = MixpanelService.getUrls();

            // Merge the base URLs into event properties
            const finalEvent = {
                ...newEvent,
                properties: {
                    ...mixpanelBaseProps,
                    ...newEvent.properties
                }
            };

            // Find the index of the event with the same 'event' value
            const eventIndex = state.eventsToTrack.findIndex(
                event => event.event === finalEvent.event
            );

            if (!finalEvent.nonUniqueEvent && eventIndex !== -1) {
                // If the event exists, update it with the newEvent
                state.eventsToTrack[eventIndex] = finalEvent;
            } else {
                // If the event doesn't exist, add the newEvent to the eventsToTrack array
                state.eventsToTrack = [...state.eventsToTrack, finalEvent];
            }

        },
        clearEventsToTrackList(state) {
            state.eventsToTrack = [];
        },
        updateSidePanelVisibility(state, val) {
            state.showSidePanel = val;
        },
        updateSelectedSectionImage(state, newImageObj) {
            store.state.selectedSection.image = Object.assign({}, store.state.selectedSection.image, newImageObj);
        },
        updateSelectedSectionMedia(state, newMediaObj) {
            if (state.activeModalData?.moduleId && state.selectedSection?.type === SectionTypesEnum.GENERAL) {
                const module = getModuleContent(state.selectedSection, state.activeModalData?.moduleId);
                if (module) module.media = {...module.media, ...newMediaObj};
                return;
            }
            if(typeof store.state.selectedColumnIndex === 'number') {
                switch (store.state.selectedSection.type) {
                    case 'Columns':
                        state.selectedSection['columns'][state.selectedColumnIndex]['media'] = newMediaObj
                        break;
                    case 'Testimonial':
                        state.selectedSection['testimonial'][state.selectedColumnIndex]['media'] = newMediaObj
                        break;
                    case 'LogoWall':
                        state.selectedSection['logos'][state.selectedColumnIndex] = {
                            ...state.selectedSection['logos'][state.selectedColumnIndex],
                            ...newMediaObj
                        }
                        break;
                }
            } else {
                store.state.selectedSection.media = Object.assign({}, store.state.selectedSection.media, newMediaObj);
            }
        },
        updateSectionMediaBasedOnActiveModalData(state, newObj) {
            if (state.activeModalData?.moduleId && state.selectedSection?.type === SectionTypesEnum.GENERAL) {
                const module = getModuleContent(state.selectedSection, state.activeModalData?.moduleId);
                if (module) module.media = newObj;
                return;
            }
            if(!state.activeModalData.column) {
                const isPortfolioHero = store.getters.selectedSectionIsPortfolioHero;
                // if the image is a portfolio hero background, update portfolio hero
                // else if the image is a portfolio foreground image update portfolio.image
                // else update the selectedSection
                if(!isPortfolioHero && state.activeModalData.key === EditedImageTypeEnum.PORTFOLIO_HERO_IMAGE) {
                    state.portfolio.templateSettings.hero.image = newObj
                    state.selectedSection.backgroundColor = null;
                } else if (!isPortfolioHero && state.activeModalData.key === EditedImageTypeEnum.PORTFOLIO_HERO_MEDIA) {
                    state.portfolio.image = newObj
                } else if (state.activeModalData.key === EditedImageTypeEnum.PORTFOLIO_LOGO) {
                    store.commit("updatePortfolioLogo", { media: newObj });
                } else if (isPortfolioHero) {
                    const assetKey = state.activeModalData.key === EditedImageTypeEnum.PORTFOLIO_HERO_IMAGE ? 'image' : 'media';
                    store.commit("updatePortfolioHeroAssetByKey", { key: assetKey, payload: newObj });
                } else if (state.activeModalData.key === EditedImageTypeEnum.THUMBNAIL) {
                    state.selectedProject.image = newObj
                } else  {
                    // if this is a single column section and has image or media object
                    state.selectedSection[state.activeModalData.key] = newObj
                    if(state.activeModalData.key === EditedImageTypeEnum.IMAGE) {
                        state.selectedSection.backgroundColor = null;
                    }
                }
            } else {
                // if this is a multi-column section like Columns, Testimonial, Gallery
                if(state.activeModalData.column.key === 'columns') {
                    state.selectedSection.columns[state.activeModalData.column.index].media = newObj;
                } else if(state.activeModalData.column.key === 'tabs') {
                    state.selectedSection.tabs[state.activeModalData.column.index].media = newObj;
                } else if(state.activeModalData.column.key !== 'logos') {
                    state.selectedSection[state.activeModalData.column.key][state.activeModalData.column.index].media = newObj;
                } else {
                    // if this is a LogoWall section
                    state.selectedSection['logos'][state.activeModalData.column.index] = newObj
                }
            }
        },
        updateGalleryItems(state, newGalleryItems) {
            state.selectedSection.galleryItems = Object.assign([], state.selectedSection.galleryItems, newGalleryItems)
        },
        updateUser(state, user) {
            store.state.user = Object.assign({}, store.state.user, user);
        },
        updateUserInDb(state, field) {
            if(field) {
                event(field, {
                    'event_category': 'Profile editor',
                    'event_label':  `User: ${store.state.user._id}`
                });
            }
            state.savingProject = true;
            UsersAPI.editUser(state.user)
                .then(response => {
                    state.user = {
                        ...response.data,
                        paymentMethods:state.user.paymentMethods
                    }
                })
                .catch(error => {
                    if(error.response.status === 405) {
                        state.currentSubscriptionPlan = error.response.data.currentSubscriptionPlan;
                        this.$store.commit("showAlertMessage", {
                            message: 'Upgrade to Full access to use this feature.',
                            type: 'alert',
                            interval: 10,
                        });
                        if(error.response.data.code === 1005) {
                            state.user.accountSettings.folioLogo = true;
                        }
                    }
                })
                .finally(() => {
                   state.savingProject = false;
                });
        },
        updateUserPaymentMethods(state, paymentMethods) {
            state.user.paymentMethods = paymentMethods;
        },
        updateUserSelectedPaymentMethods(state, selectedPaymentMethod) {
            state.user.selectedPaymentMethod = selectedPaymentMethod;
        },
        updateExistingStatSectionStyles(state) {
            state.project.sections.find((item) => {
                if(item.statItems && !item.deletedAt && (item.statItems[0].data.style && item.statItems[0].data.style.textStyle || item.statItems[0].description.style && item.statItems[0].description.style.textStyle) ) {
                    return store.state.selectedStatSectionStyles = {data: item.statItems[0].data.style.textStyle, description: item.statItems[0].description.style.textStyle};
                }
            });
        },
        updateExistingHeaderSectionStyle(state) {
            store.state.project.sections.find((item) => {
                if(item.type === 'Header' && item.title.style.textStyle && !item.deletedAt ) {
                    return store.state.selectedHeaderSectionStyle = item.title.style.textStyle;
                }
            });
        },
        updateExistingTestimonialSectionStyles(state) {
            store.state.project.sections.find((item) => {
                if(item.type === 'Testimonial' && !item.deletedAt) {
                    return store.state.selectedTestimonialSectionStyles = {text: item.testimonial[0].text.style.textStyle, author: item.testimonial[0].author.style.textStyle};
                }
            });
        },
        updateFirstMockup(state) {
            store.state.project.sections.find((item) => {
                return store.state.selectedMockupStyle = item.media && item.media.mokcupSettings ? item.media.mockupSettings : (item.galleryItems && item.galleryItems[0].media.mockupSettings ? item.galleryItems[0].media.mockupSettings : defaultMockup);
            });
        },
        openSidePanel(state, data) {
            store.commit("openSidePanelOnPage",data);
        },
        openSidePanelOnPage(state, data) {
            switch(state.project.type) {
                case PageTypesEnum.HOME:
                    Router.push(`/home/portfolios/${store.state.portfolio.shortId}/${data.panelPath}`);
                    break;
                default:
                    Router.push(`/home/portfolios/${store.state.portfolio.shortId}/p/${store.state.project.shortId}/${data.panelPath}`);
                    break;
            }
        },
        closeSidePanel(state) {
            switch(state.project.type) {
                case PageTypesEnum.HOME:
                    Router.push(`/home/portfolios/${state.portfolio.shortId}`);
                    break;
                default:
                    Router.push(`/home/portfolios/${state.portfolio.shortId}/p/${state.project.shortId}`);
                    break;
            }
        },
        toggleSidePanel(state, data) {
            if(state.sidePanelSettings.allowSave) {
                store.commit("updateSidePanel", {
                    activeModal: "CancelConfirmModal",
                    goToPanel: data.panelPath
                })

                return
            }

            if(location.pathname.indexOf(`/${data.panelPath}`) < 0) {
                store.commit("openSidePanel", data)
            } else {
                store.commit("closeSidePanel");
            }
        },
        navigateBetweenSidePanels(state, data) {
            // if there is no data or data.mainPanel the router goes to Design panel
            // if there is no data.targetPanelPath the router goes to main panel
            // targetPanelPath should start with /
            let path;
            const mainPanel = data && data.mainPanel ? data.mainPanel : DesignPanelPathsEnum.ROOT
            switch(state.project.type) {
                case PageTypesEnum.HOME:
                    path = `/home/portfolios/${state.portfolio.shortId}/${mainPanel}`;
                    break;
                default:
                    path = `/home/portfolios/${state.portfolio.shortId}/p/${state.project.shortId}/${mainPanel}`
            }

            if(!data || data && !data.targetPanelPath) return Router.push(path);

            return Router.push(path + data.targetPanelPath);

        },
        setChatBubblePosition(state, x) {
            const widgetContainer = document.querySelector('#freshworks-container');
            const widgetLauncher = document.querySelector('#launcher-frame');

            if(!!widgetContainer) {
                widgetContainer.style.cssText += `--leftPos: ${x}px;`;
                if(store.getters.showTrialPrompt) widgetContainer.style.cssText += `--bottomBubPos: 51px; --bottomPopPos: 109px`;
            }
            if(!!widgetLauncher) {
                widgetLauncher.style.cssText += `--leftPos: ${x}px;`;
            }
        },
        async openPortfolioToEdit(state, portfolioId) {
            await PortfolioActions.getPortfolios(portfolioId);
            store.commit("setPageToEdit", "home");
            await Router.push(`/home/portfolios/${state.portfolio.shortId}`)
        },
        setHeightOfTextareas(state) {
            autosize(document.querySelectorAll('textarea'));
            setTimeout(() => { autosize.update(document.querySelectorAll('textarea')) }, 10);
        },
        scrollToSection(state, data) {
            const maxAttempts = data?.attempts ?? 10;
            let attempt = 0;

            const tryScroll = () => {
                const s = document.getElementById(`section-${data.sectionId}`);
                if(!s && attempt < maxAttempts) {
                    attempt += 1;
                    return setTimeout(tryScroll, 80);
                }
                if(!s) return;

                s.classList.toggle('restore.stated', !!data.restoreSection);
                const scrollVal = offset(s) - (s.offsetHeight <= window.innerHeight ? ((window.innerHeight - s.offsetHeight) / 2 + 29) : 58);
                window.scrollTo({
                    top: scrollVal,
                    left: 0,
                    behavior: 'smooth'
                });
            };

            const offset = (el) => {
                const rect = el.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                return rect.top + scrollTop
            };

            tryScroll();
        },
        showAlertMessage(state, data) {
            state.alertMessage = {
                type: data.type,
                message: data.message,
                show: data.show === false ? data.show : true,
                interval: data.interval ? data.interval : 3,
            }
        },
        showAiFeedbackModal(state, data) {
            state.showAiFeedbackModal = true;
            state.aiFeedbackModalType = data.type;
        },
        hideAiFeedbackModal(state) {
            state.showAiFeedbackModal = false;
            state.aiFeedbackModalType = null;
        },
        scrollToItem(state, item) {
            let scrollToItem = item.index-1 > -1 ? document.querySelector(`[data-id="${item.index-1}"]`) : null;
            let scrollToSection = document.querySelector(`[data-section-index="section-${store.state.selectedSection.index}"]`);
            let scrollVal = scrollToSection.offsetTop - (scrollToSection.offsetHeight <= window.innerHeight ? ((window.innerHeight - scrollToSection.offsetHeight) / 2 + 29) : 58) + (scrollToItem ? scrollToItem.offsetTop : 0);
            window.scrollTo({top: scrollVal, left: 0, behavior: 'smooth'});
        },
        updatePortfolioInUserPortfolios(state, portfolio) {
            const portfolioIndex = state.userPortfolios.findIndex(p => p.shortId === portfolio.shortId)
            state.userPortfolios[portfolioIndex] = portfolio;
        },
        openLink(state, link) {
            window.open(link);
        },
        recalculateScrollTriggers(state) {
            state.recalculateScrollTrigger = !state.recalculateScrollTrigger;
        },
        updateChecklistItem(state, step) {
            //Only do anything if value not null. For all old users value will be null
            if(state.user.checklistStatus) {
                state.user.checklistStatus.steps[step] = true;
                state.checklist.activeInteraction = null;

                let action = 'Finished: ' + step;
                event(action, {
                    'event_category': 'Activation checklist',
                    'event_label': `User: ${state.user._id}`
                });

                MixpanelService.track("CHECKLIST_STEP_COMPLETED",
                    {
                        "event_group": MixpanelService.MixpanelEventGroups.ONBOARDING,
                        "source": "Checklist",
                        "item_name": step
                    }
                )

                store.commit('updateUserInDb');
            }
        },
        updateChecklistFinished(state, finished) {
            state.checklist.finished = finished;
        },
        updateActivationPopupState(state, value) {
            state.checklist.popUpClosed = value;
        },
        setPortfolioPublishedState(state, value) {
            if (state.portfolio) {
                state.portfolio.draft = value;
            }
            if (state.selectedPortfolio) {
                state.selectedPortfolio.draft = value;
            }
        },
        setPortfoliosToDraft(state) {
            if (state.userPortfolios && state.userPortfolios.length) {
                state.userPortfolios.forEach(portfolio => {
                    portfolio.draft = true;
                })
            }
            if (state.portfolio) {
                state.portfolio.draft = true;
            }
            if (state.selectedPortfolio) {
                state.selectedPortfolio.draft = true;
            }
        },
        setPortfolioCurrentJobFitCheckId(state, value) {
            state.portfolio.currentJobFitCheckId = value;
            state.userPortfolios.filter((c) => {
                return c._id === state.portfolio._id
            })[0].currentJobFitCheckId = value;
        },
        setBlockedEditor(state, blockedEditor) {
            state.blockedEditor = blockedEditor;
        },
        setSubscriptionExpiredModalShown(state) {
            state.subscriptionExpiredModalShown = true;
        },
        setTextEditorActive(state, active) {
            state.textEditorActive = active;
        },
        setGlobalCTSStatus(state, status) {
            state.isGlobalCtsOff = status;
        },
        decreaseFeatureToken(state, feature) {
            const current = state.user.featureTokens[feature];
            state.user.featureTokens[feature] = Math.max(0, current - 1)
        },
        // design related mutations
        UPDATE_DESIGN_SETTINGS(state, { newValue, keyToItem, keyToDesignItem }) {
            const target = getDeep(state, keyToItem);
            if (typeof target.designSettings[keyToDesignItem] === 'object' && target.designSettings[keyToDesignItem] !== null) {
                target.designSettings[keyToDesignItem] = {
                    ...target.designSettings[keyToDesignItem],
                    ...newValue
                };
            } else {
                target.designSettings[keyToDesignItem] = newValue;
            }
        },
        INITIALIZE_CASE_STUDY_COLORS_FROM_PORTFOLIO(state) {
            const portfolioDesignSettings = state.portfolio.designSettings;
            const projectDesignSettings = state.project.designSettings;

            ["colors", "colorStyles", "colorPalette", "colorPaletteId"].forEach((key) => {
                projectDesignSettings[key] = cloneDesignSetting(portfolioDesignSettings[key]);
            });
            projectDesignSettings.globalColorStyles = false;
        },
        INITIALIZE_CASE_STUDY_FONTS_FROM_PORTFOLIO(state) {
            const portfolioDesignSettings = state.portfolio.designSettings;
            const projectDesignSettings = state.project.designSettings;

            ["fontStyles", "fontPairId"].forEach((key) => {
                projectDesignSettings[key] = cloneDesignSetting(portfolioDesignSettings[key]);
            });
            projectDesignSettings.globalFontStyles = false;
        },
        UPDATE_CTA_STYLE(state, { newValue, editedCtaType, keyToItem, keyToObject }) {
            state[keyToItem][keyToObject][editedCtaType] = newValue;
        },
        UPDATE_LABEL_STYLE(state, { newValue, keyToItem, keyToObject }) {
            state[keyToItem][keyToObject].labelStyle = newValue;
        },
        setTriggerAnimation(state, value) {
            state.triggerAnimation = value;
        },
        setFontPairs(state, value) {
            state.fontPairs = value;
        },
        setCursorPreviewMode(state, value) {
            state.cursorPreviewMode = value;
        }
    },
    getters: {
        ...portfolioGetters,
        ...projectGetters,
        ...sectionGetters,
        ...sidebarGetters,
        ...templateGetters,
        isEditorBlocked() {
            return !!store.state.blockedEditor;
        },
        isTextEditorActive() {
            return store.state.textEditorActive;
        },
        countPublishedPortfolios() {
            return store.state.userPortfolios.filter(portfolio => !portfolio?.draft).length
        },
        countPublishedCaseStudies() {
            return store.state.caseStudyPages.filter(caseStudy => !caseStudy?.draft).length
        },
        isDefaultAccentColor() {
            if(!store.state.project.settings || store.state.project.settings.colors && !store.state.project.settings.colors.accent) {
                return true;
            }
        },
        userIsTrial() {
            return store.state.currentSubscriptionPlan === 'Trial';
        },
        userIsFreeOrTrial() {
            return store.state.currentSubscriptionPlan === 'Free' || store.state.currentSubscriptionPlan === 'Trial'
        },
        userIsPremium() {
            return !store.getters.userIsFreeOrTrial && !store.getters.userIsCanceledPremium && !store.getters.userIsPastDue;
        },
        userIsPastDue() {
            return store.state.userSettings.currentSubscriptionPlan === 'Premium' && store.state.userSettings.currentSubscription?.status === 'past_due'
        },
        userIsCanceledPremium() {
            return store.state.userSettings.currentSubscriptionPlan === 'Premium' && store.state.userSettings.currentSubscription?.status === 'canceled'
        },
        userIsVIP() {
            return store.state.currentSubscriptionPlan === 'VIP'
        },
        subscriptionBannerState(state) {
            const sub = state.userSettings?.currentSubscription;
            const plan = state.userSettings?.currentSubscriptionPlan || state.currentSubscriptionPlan;
            const targetTime = sub?.nextBillingDate ? new Date(sub.nextBillingDate).getTime() : null;
            const now = Date.now();
            const msDay = 1000 * 60 * 60 * 24;

            const trialing = plan === "Trial" || sub?.status === "trialing";
            if (trialing) {
                if (targetTime && targetTime > now) {
                    const daysLeft = Math.floor((targetTime - now) / msDay);
                    const dayNumber = Math.min(7, Math.max(1, 7 - daysLeft));
                    return `Trial day ${dayNumber}`;
                }
                return "Trial expired";
            }

            if (sub?.status === "past_due" || sub?.status === "canceled") {
                return "Subscription expired";
            }

            return undefined;
        },
        userHasAiToken: (state) => (feature) => {
            return state.user.featureTokens[feature] > 0;
        },
        caseStudies(state) {
            return state.caseStudyPages
        },
        userIsAdmin(state) {
            return state.user && (state.user.isAdmin || state.user.isTemplateCreator)
        },
        isAdminPresetEditor(state, getters) {
            return !!getters["presetEditor/isActive"];
        },
        pageViewURL(state) {
            const root = !!state.portfolio.ownDomainAddress && !state.project.draft ? `https://${state.portfolio.ownDomainAddress}` : location.origin;
            const subPagePath = Router.currentRoute.value.params.projectShortId;
            let url = !!state.portfolio.ownDomainAddress ? root : `${root}/${state.portfolio.shortId}`;

            // set path
            if(!!subPagePath) {
                if(!!state.portfolio.ownDomainAddress && !state.project.draft) {
                    url = `${root}/p/${subPagePath}`
                } else {
                    url = `${root}/p/${state.portfolio.shortId}/${subPagePath}`
                }
            }

            return url
        },
        isHomePage(state) {
            return state.project && state.project.type === PageTypesEnum.HOME;
        },
        isCaseStudy(state) {
            return state.project && state.project.type === PageTypesEnum.CASE_STUDY;
        },
        ctsIsActive(state) {
            return state.sectionEditorSettings.ctsModuleSelected
        },
        designSettings(state, getters) {
            return getters.isCaseStudy && !getters.usingGlobalColorStyles
                ? state.project.designSettings
                : state.portfolio.designSettings;
        },
        fontStyles(state, getters) {
            return getters.isCaseStudy && !getters.usingGlobalFontStyles
                ? state.project.designSettings.fontStyles
                : state.portfolio.designSettings.fontStyles;
        },
        colorStyles(state, getters) {
            return getters.isCaseStudy && !getters.usingGlobalColorStyles
                ? state.project.designSettings.colorStyles
                : state.portfolio.designSettings.colorStyles;
        },
        colorPalette(state, getters) {
            const colorStyles = getters.colorStyles;

            if (getters.designSettings.colorPalette?.colors?.length > 0) {
                return getters.designSettings.colorPalette;
            }

            // Default colors if colorPalette is empty or not defined
            return {
                colors: [
                    colorStyles.heroTitle.primaryColor || colorStyles.heroTitle.color,
                    colorStyles.heroParagraph.primaryColor || colorStyles.heroParagraph.color,
                    colorStyles.hyperLink.primaryColor || colorStyles.hyperLink.color,
                    colorStyles.navigation.primaryColor || colorStyles.navigation.color,
                    colorStyles.background.primaryColor || colorStyles.background.color,
                ]
            }
        },
        showTrialPrompt(state, getters) {
            return !Router.currentRoute.value.meta.nonEditorPage
                && !state.onboardingFinished
                && state.userSettings.currentSubscriptionPlan
                && !getters.userIsVIP
                && (getters.userIsTrial || !getters.userIsPremium)
        },
        activeChecklistStepsStatus(state) {
            const steps = state.user.checklistStatus.steps;
            const allowed = new Set(state.activeChecklistItems);
            return Object.fromEntries(
                Object.entries(steps).filter(([key]) => allowed.has(key))
            );
        },
        userHasUpcomingDiscount(state) {
            const upComingInvoice = state.userSettings?.currentSubscription?.upComingInvoice;
            return !!(upComingInvoice?.amountOff || upComingInvoice?.percentOff);
        }
    },
    actions: {
        ...sectionActions,
        async addSection(context, payload) {
            if (context.getters.isAdminPresetEditor) {
                return context.dispatch("presetEditor/addSectionLocal", payload);
            }
            return sectionActions.addSection(context, payload);
        },
        async updateSection(context, payload) {
            if (context.getters.isAdminPresetEditor) {
                return context.dispatch("presetEditor/updateSectionLocal", payload);
            }
            return sectionActions.updateSection(context, payload);
        },
        async updateSections(context, payload) {
            if (context.getters.isAdminPresetEditor) {
                return context.dispatch("presetEditor/updateSectionsLocal", payload);
            }
            return sectionActions.updateSections(context, payload);
        },
        async archiveSection(context, payload) {
            if (context.getters.isAdminPresetEditor) {
                return context.dispatch("presetEditor/archiveSectionLocal", payload);
            }
            return sectionActions.archiveSection(context, payload);
        },
        async reorderSections(context, payload) {
            const sections = Array.isArray(payload) ? payload : payload.sections;
            const keepModalOpen = !Array.isArray(payload) && payload.keepModalOpen;
            if (context.getters.isAdminPresetEditor) {
                return context.dispatch("presetEditor/reorderSectionsLocal", sections);
            }
            return sectionActions.reorderSections(context, sections, keepModalOpen);
        },
        async duplicateSection(context, payload) {
            if (context.getters.isAdminPresetEditor) {
                return context.dispatch("presetEditor/duplicateSectionLocal", payload);
            }
            return sectionActions.duplicateSection(context, payload);
        },
        async saveSectionMediaChange(context, { section = null, modal = "keepModalOpen" } = {}) {
            const targetSection = section || context.state.selectedSection;
            if (!targetSection) return;
            return context.dispatch("updateSection", { section: targetSection, modal });
        },
        async uploadCroppedMedia(context, { blob, extension, imageToDelete = null, forcePortfolio = false } = {}) {
            if (!blob || !extension) {
                throw new Error("uploadCroppedMedia: blob and extension are required");
            }

            const userId = context.state.user?._id;
            const isPresetMode = context.getters.isAdminPresetEditor;

            if (isPresetMode) {
                return AdminAPI.uploadSectionPresetImage(userId, blob, extension, imageToDelete);
            }

            const usePortfolioUpload = forcePortfolio || isPresetMode;

            if (usePortfolioUpload) {
                const portfolioId = context.state.portfolio?._id;
                if (!userId || !portfolioId) {
                    throw new Error("uploadCroppedMedia: missing user/portfolio context");
                }
                return PortfoliosAPI.uploadCroppedImage(userId, portfolioId, blob, extension, imageToDelete);
            }

            const projectId = context.state.project?._id;
            if (!userId || !projectId) {
                throw new Error("uploadCroppedMedia: missing user/project context");
            }
            return ProjectsAPI.uploadCroppedImage(userId, projectId, blob, extension, imageToDelete);
        },
        async deleteMediaAsset(context, { imageUrl = null, croppedImageUrl = null, forcePortfolio = false } = {}) {
            if (!imageUrl && !croppedImageUrl) return;

            const cleanupRequestOptions = { suppressAuthStatus: true };
            const isPresetMode = context.getters.isAdminPresetEditor;
            if (isPresetMode) {
                const primaryUrl = imageUrl || croppedImageUrl;
                const secondaryUrl = imageUrl && croppedImageUrl && imageUrl !== croppedImageUrl ? croppedImageUrl : null;
                try {
                    await AdminAPI.deleteSectionPresetImage(primaryUrl, secondaryUrl, cleanupRequestOptions);
                } catch (error) {
                    console.error(error);
                }
                return;
            }

            const usePortfolioDelete = forcePortfolio || isPresetMode;

            if (usePortfolioDelete) {
                const portfolioId = context.state.portfolio?._id;
                if (!portfolioId) return;

                const requests = [];
                if (imageUrl) {
                    requests.push(PortfoliosAPI.deletePortfolioImage(portfolioId, imageUrl, cleanupRequestOptions));
                }
                if (croppedImageUrl && croppedImageUrl !== imageUrl) {
                    requests.push(PortfoliosAPI.deletePortfolioImage(portfolioId, croppedImageUrl, cleanupRequestOptions));
                }
                if (!requests.length) return;

                try {
                    await Promise.all(requests);
                } catch (error) {
                    console.error(error);
                }
                return;
            }

            return context.dispatch("deleteProjectImage", { imageUrl, croppedImageUrl, options: cleanupRequestOptions });
        },
        async trackAndClearEvents({commit, state}) {
            try {
                if (state.eventsToTrack.length === 0) {
                    return;
                }

                for (const e of state.eventsToTrack) {
                    await MixpanelService.track(e.event, e.properties); // assuming MixpanelService.track is async
                }

                commit('clearEventsToTrackList');
            } catch (error) {
                console.error('Error while tracking events:', error);
            }
        },
        showChatBubble() {
            let path = Router.currentRoute.value.fullPath;
            if (window.fwSettings && !store.state.onboardingFinished && !path.includes("/home/onboarding")) {
                setTimeout(() => {
                    window.FreshworksWidget('show', 'launcher');
                }, 5000);
            }
        },

        // portfolio related actions
        async updatePortfolio({commit, state}, {field, skipStoreUpdate = false, keepModalOpen = false}) {
            // Optional: call analytics tracking
            if (field) {
                PortfolioActions.trackEditEvent(field);
            }

            commit('updateSavingState');

            try {
                const response = await PortfoliosAPI.editPortfolio(state.portfolio);

                if (!skipStoreUpdate) {
                    commit('setPortfolio', response.data);
                    commit('setSelectedPortfolio', response.data);
                    commit('updatePortfolioInUserPortfolios', response.data);
                }

            } catch (error) {
                console.error(error);
                commit('setActiveModal', null);

                const errorObject = error.response?.data;

                if (error.response?.status === 405 && errorObject?.code === 1007) {
                    commit('setCurrentPlan', errorObject.currentSubscriptionPlan);
                    commit('showAlertMessage', {
                        message: 'Upgrade to Full access to use this feature.',
                        type: 'alert',
                        interval: 10,
                    });
                }

            } finally {
                if (!keepModalOpen) {
                    commit('setActiveModal', null);
                }
                commit('updateSavingState', false);
                commit('updateSidePanel', {allowSave: false});
            }
        },

        // section related actions
    },

});

store.subscribe((mutation, state) => {
    if (mutation.type === 'setBlockedEditor') {
        if (mutation.payload) {
            state.showSidePanel = false;
            store.commit("setPortfoliosToDraft");

            const currentPath = Router.currentRoute.value.fullPath;
            if (!['/home/prices', '/home/settings', '/home/payment'].some(path => currentPath.includes(path))
                && !state.subscriptionExpiredModalShown
                && state.userSettings.currentSubscriptionPlan !== 'VIP'
                && !(state.user?.isAdmin || state.user?.isTemplateCreator)) {
                store.commit("setActiveModal", "SubscriptionExpiredModal");
            }
        }
    }
});

function getDeep(state, path) {
    return path.split('.').reduce((acc, key) => acc?.[key], state);
}
function setDeep(state, path, value) {
    const keys = path.split('.');
    const last = keys.pop();
    const target = keys.reduce((acc, key) => acc?.[key], state);
    if (target && last) target[last] = value;
}
